import React, { useEffect, useRef, useState } from 'react';
import styles from '../../../src/assets/scss/OrderTable.module.scss';
import { FaEye, FaEdit, FaEllipsisV, FaChevronUp, FaChevronDown, FaPrint } from 'react-icons/fa';
import Pagination from '../../Components/Common/Pagination';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGetMyEmployeesQuery,
  useGetOrdersQuery,
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation
} from '../../slices/admin/admin';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserInfo } from '../../utils/user.util';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import MyOrders from './MyOrders';
import PopupWrapper from '../../Components/Common/PopupWrapper';
import OrderPrintDesign from './OrderPrintDesign';
import { capitalize } from '../../utils/capitalize';

const OrderTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [limit, setLimit] = useState(10);
  const [showStats, setShowStats] = useState(false);

  // print popup
  const [showPrintPopup, setShowPrintPopup] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTabs, setActiveTabs] = useState("thermal");

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "orderOverview";
  const setActiveTab = (tab) => setSearchParams({ tab });

  const navigate = useNavigate();
  const user = getUserInfo();
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${styles.dropdownWrapper}`)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ✅ FIX: Force retailowner & customer to "myInvoice"
  useEffect(() => {
    if (
      (user?.type === "retailowner" || user?.type === "customer") &&
      activeTab === "orderOverview"
    ) {
      setActiveTab("myInvoice");
    }
  }, [user?.type, activeTab]);

  const [updateOrderStatus] = useUpdateOrderStatusMutation();
  const [updateOrderByStore] = useUpdateOrderMutation();

  const orderPermissions =
    user?.roles?.flatMap(role =>
      role.permissions?.filter(p => p.functionality === 'orders')
    ) || [];

  const permissionTypes = orderPermissions.map(p => p.permissionType);

  const hasFullAccess = permissionTypes.includes('fullaccess');
  const canEdit = hasFullAccess || permissionTypes.includes('update');
  const canView = hasFullAccess || permissionTypes.includes('view only');
  const canStatusUpdate = hasFullAccess || permissionTypes.includes('status update');
  const isEmployeeOrderRole = user?.roles?.some(role => role.roleName === 'employee_order');

  const {
    data,
    isLoading,
    isError
  } = useGetOrdersQuery({
    offset: currentPage,
    limit,
    status,
    orderowner: search,
    fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : undefined,
    toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : undefined
  });

  const { data: empData, isLoading: isEmpLoading } = useGetMyEmployeesQuery();
  const employees = empData?.result || [];

  useEffect(() => {
    setCurrentPage(1);
  }, [search, fromDate, toDate, status]);

  const getAssignedEmployeeName = (id) => {
    const employee = employees.find(emp => emp._id === id);
    return employee ? `${employee.firstName} ${employee.lastName}` : '-';
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus({ orderId, status: newStatus }).unwrap();
      toast.success('Order status updated!');
      setDropdownOpen(null);
    } catch (err) {
      toast.error('Failed to update status');
      console.error(err);
    }
  };

  const orders = data?.result || [];
  const totalOrders = data?.total || 0;
  const totalPages = Math.ceil(totalOrders / limit);

  const handleCreateOrder = () => navigate('/dashboard/CreateOrders');

  return (
    <div className={styles.orderContainer}>
      <div className={styles.ordersSection}>
        <div className={styles.tabButtons}>
          <div className={styles.dflex}>
            {/* Show Order Overview only for non-retail & non-customer */}
            {user?.type !== "retailowner" && user?.type !== "customer" && (
              <button
                className={`${styles.tabButton} ${activeTab === "orderOverview" ? styles.active : ""}`}
                onClick={() => setActiveTab("orderOverview")}
              >
                Orders List & Create
              </button>
            )}

            <button
              className={`${styles.tabButton} ${activeTab === "myInvoice" ? styles.active : ""}`}
              onClick={() => setActiveTab("myInvoice")}
            >
              My Orders
            </button>
          </div>

          <div>
            <button
              className={styles.collapseBtn}
              onClick={() => setShowStats(prev => !prev)}
            >
              {showStats ? <FaChevronUp className={styles.collapseBtnicon} /> : <FaChevronDown className={styles.collapseBtnicon} />}
            </button>
          </div>
        </div>

        {/* Order Overview Section */}
        {user?.type !== "retailowner" && user?.type !== "customer" && activeTab === 'orderOverview' && (
          <>
            <div className={styles.statsCards}>
              {showStats && (
                <>
                  <div className={`${styles.card} ${styles.total}`}>
                    <h4 className={styles.margin}>Total Orders</h4>
                    <h2 className={styles.marginnum}>{totalOrders}</h2>
                    <p className={styles.marginday}>Current Month</p>
                  </div>
                  <div className={`${styles.card} ${styles.preorder}`}>
                    <h4 className={styles.margin}>Pre Order Count</h4>
                    <h2 className={styles.marginnum}>{data?.preOrderCount || 0}</h2>
                    <p className={styles.marginday}>Current Month</p>
                  </div>
                  <div className={`${styles.card} ${styles.attention}`}>
                    <h4 className={styles.margin}>Order Need Attention</h4>
                    <h2 className={styles.marginnum}>{data?.needAttentionCount || 0}</h2>
                    <p className={styles.marginday}>Current Month</p>
                  </div>
                </>
              )}

              <div className={styles.filterRow}>
                <div className={styles.filterInputs}>
                  <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search by Order Owner..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <select className={styles.inputs} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="movetobilling">Move to Billing</option>
                    <option value="delivered">Delivered</option>
                  </select>

                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="From Date"
                    maxDate={new Date()}
                    isClearable
                    className={styles.inputss}
                  />
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="To Date"
                    maxDate={new Date()}
                    isClearable
                    className={styles.inputss}
                  />
                  <button
                    className={styles.clearBtn}
                    onClick={() => {
                      setSearch('');
                      setStatus('');
                      setFromDate(null);
                      setToDate(null);
                      setCurrentPage(1);
                    }}
                  >
                    Clear All
                  </button>
                  {hasFullAccess && (
                    <button className={styles.addBtn} onClick={handleCreateOrder}>
                      + Create Orders
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.orderTable}>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Order From</th>
                      <th> Pre Order Date</th>
                      <th> Order Date</th>
                      <th>Order Status</th>
                      <th>Created By & For</th>
                      {hasFullAccess && <th>Assigned To</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr><td colSpan={10}>Loading...</td></tr>
                    ) : isError ? (
                      <tr><td colSpan={10}>Failed to load orders.</td></tr>
                    ) : orders.length === 0 ? (
                      <tr><td colSpan={10} className={styles.noData}>No orders found.</td></tr>
                    ) : (
                      orders.map((order, index) => (
                        <tr key={order._id || index}>
                          <td className="notranslate">{order.orderId}</td>
                          <td>{(order.store?.storeName || order.orderStore?.storeName) ? capitalize(order.store?.storeName || order.orderStore?.storeName) : `${order.createdBy?.firstName || ''} ${order.createdBy?.lastName || ''}`.trim() || '-'}</td>
                          <td>{order.date ? new Date(order.date).toLocaleDateString() : '-'}</td>
                          <td>{order.items?.[0]?.createdAt ? new Date(order.items[0].createdAt).toLocaleDateString() : '-'}</td>
                          <td>
                            <span className={`${styles.status} ${
                                order.status?.toLowerCase() === "approved" || order.status?.toLowerCase() === "completed" || order.status?.toLowerCase() === "delivered" || order.status?.toLowerCase() === "accepted"
                                  ? styles.statusApproved
                                  : order.status?.toLowerCase() === "pending"
                                  ? styles.statusPending
                                  : order.status?.toLowerCase() === "readyforbilling" || order.status?.toLowerCase() === "readyforpickup" || order.status?.toLowerCase() === "movetobilling"
                                  ? styles.statusWarning
                                  : order.status?.toLowerCase() === "rejected" || order.status?.toLowerCase() === "cancelled"
                                  ? styles.statusRejected
                                  : styles.statusDefault
                              }`}>
                              {order.status ? capitalize(order.status) : '-'}
                            </span>
                          </td>
                          <td>{`${order.createdBy?.firstName ? capitalize(order.createdBy.firstName) : ''} ${order.createdBy?.lastName ? capitalize(order.createdBy.lastName) : ''}`.trim() || '-'}</td>

                          {hasFullAccess && (
                            <td>
                              <select
                                className={styles.inputsselect}
                                value={order.assignedTo || ''}
                                disabled={order.status === "readyforBilling"}
                                onChange={async (e) => {
                                  const selectedId = e.target.value;
                                  const orderId = order._id;
                                  const updatePayload = {
                                    assignedTo: selectedId,
                                    preOrderDate: order.date,
                                    products: order.items.map(item => ({
                                      _id: item._id,
                                      productName: item.productName,
                                      productQuantity: item.productQuantity,
                                      productCount: item.productCount,
                                      productBag: item.productBag,
                                    })),
                                    notes: order.notes || '',
                                  };

                                  try {
                                    await updateOrderByStore({ id: orderId, body: updatePayload }).unwrap();
                                    toast.success('Employee assigned successfully!');
                                  } catch (err) {
                                    toast.error('Failed to assign employee.');
                                    console.error('Update failed:', err);
                                  }
                                }}
                              >
                                <option value="">Select Employee</option>
                                {employees.map((emp) => (
                                  <option key={emp._id} value={emp._id}>
                                    {emp.firstName} {emp.lastName} ({emp.userName})
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}

                          <td>
                            <div className={styles.actions}>
                              {canEdit && order?.status !== 'readyforBilling' && order?.status !== 'completed' && (
                                <FaEdit
                                  size={16}
                                  className={styles.icon}
                                  onClick={() => {
                                    localStorage.removeItem('orderItems');
                                    localStorage.removeItem('selectedStore');
                                    localStorage.removeItem('selectedDate');
                                    navigate('/dashboard/CreateOrders', {
                                      state: { updateMode: true, orderId: order._id }
                                    });
                                  }}
                                />
                              )}

                              {canView && (
                                <FaEye
                                  size={16}
                                  className={styles.icon}
                                  onClick={() =>
                                    navigate('/dashboard/CreateOrders', {
                                      state: { viewOnly: true, orderId: order._id }
                                    })
                                  }
                                />
                              )}

                              <FaPrint
                                className={styles.icon}
                                onClick={() => {
                                  setSelectedBill(order);
                                  setActiveTabs("thermal");
                                  setShowPrintPopup(true);
                                }}
                              />

                              {/* {canStatusUpdate && order?.status === "pending" && (
                                <button
                                  className={styles.approveButton}
                                  onClick={() =>
                                    navigate("/dashboard/ApproveOrders", {
                                      state: { viewOnly: true, orderId: order._id },
                                    })
                                  }
                                >
                                  Assign Items
                                </button>
                              )} */}



                              {canStatusUpdate && order?.status === "pending" && (
                                <button
                                  className={styles.approveButton}
                                  onClick={() => {
                                    const preOrderDate = new Date(order.date);
                                    const today = new Date();

                                    today.setHours(0, 0, 0, 0);
                                    preOrderDate.setHours(0, 0, 0, 0);

                                    const tomorrow = new Date(today);
                                    tomorrow.setDate(today.getDate() + 1);

                                    if (preOrderDate.getTime() <= tomorrow.getTime()) {
                                      navigate("/dashboard/ApproveOrders", {
                                        state: { viewOnly: true, orderId: order._id },
                                      });
                                    } else {
                                      toast.warning(
                                        `Pre Order Date: ${preOrderDate.toLocaleDateString()}. You can assign items only for past, today, or tomorrow orders.`
                                      );
                                    }
                                  }}
                                >
                                  Assign Items
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className={styles.paginationWrapper}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                />
                <select
                  className={styles.inputsbtn}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 rows</option>
                  <option value={25}>25 rows</option>
                  <option value={50}>50 rows</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* My Orders Section */}
        {activeTab === 'myInvoice' && (
          <div className={styles.tabContent}>
            <MyOrders />
          </div>
        )}

        {/* Print Popup */}
        {showPrintPopup && selectedBill && (
          <PopupWrapper onClose={() => setShowPrintPopup(false)}>
            <div className={styles.popupHeader}>
              <div className={styles.headers}>
                <h2 className={styles.tittle}>Share</h2>
                <span className={styles.closeIcon} onClick={() => setShowPrintPopup(false)}>✕</span>
              </div>

              <div className={styles.tabHeader}>
                <button
                  className={activeTabs === "thermal" ? styles.activeTabs : ""}
                  onClick={() => setActiveTabs("thermal")}
                >
                  Thermal Print
                </button>
                <button
                  className={activeTabs === "whatsapp" ? styles.activeTabs : ""}
                  onClick={() => setActiveTabs("whatsapp")}
                >
                  WhatsApp Share
                </button>
              </div>

              <div className={styles.tabContent}>
                {activeTabs === "thermal" && (
                  <div className={styles.invoicePreview}>
                    <OrderPrintDesign mode="thermal" data={selectedBill} />
                  </div>
                )}

                {activeTabs === "whatsapp" && (
                  <div className={styles.whatsappShare}>
                    <OrderPrintDesign mode="whatsapp" data={selectedBill} />
                  </div>
                )}
              </div>
            </div>
          </PopupWrapper>
        )}
      </div>
    </div>
  );
};

export default OrderTable;

